import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, KundeAkte, Unternehmen } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useBeforeUnload, useBestaetigung, useFormularZustand }
  from "@kundeninfo/ui";
import { Auswahl, Chip, Formular, Hinweis } from "@kundeninfo/ui/me-design";
import { hausfarbeChip } from "../markenfarben.js";
import { KiKnopf } from "../components/meHuellen.js";
import { BeziehungRechnungsangaben } from "./BeziehungRechnungsangaben.js";
import type { RechnungsangabenClient } from "./BeziehungRechnungsangaben.js";
import { PARTNER_TEXT } from "./partners/texts.js";
import { PartnerRoleBadges, relationsSentence } from "./partners/PartnerRoleBadges.js";
import { ChangeRoles, CreditorNumber } from "./partners/RelationRoles.js";
import type { RelationRolesClient } from "./partners/RelationRoles.js";
import { RoleChoiceField, hasRole, rolesOf } from "./partners/RoleChoice.js";
import type { RoleChoice } from "./partners/RoleChoice.js";
import "./KundeBeziehungen.css";

/**
 * Der Abschnitt „Unternehmenszuordnung" in den Stammdaten eines Kunden (Kleinpakete II,
 * P1, Entscheid Dirk 18.09.2026).
 *
 * Er beantwortet die Frage, die bisher nur beim ANLEGEN beantwortet werden konnte: Bei
 * WELCHEN Konzernunternehmen ist diese Firma Kunde — und wie kommt ein weiteres dazu.
 * Bis heute ging das nur über den Umweg „Kunde anlegen" mit derselben Firma und der
 * Lenkung aus der Dublettensuche; an einem längst angelegten Kunden gab es den Weg gar
 * nicht.
 *
 * **Ein eigener Block unter den Stammdaten, kein Teil von ihnen** (dieselbe Bauart wie
 * `KundeKonditionen` und `KundePortalMandanten`): Die Kundenbeziehung ist ein EIGENER
 * Satz, der angelegt wird — sie hängt nicht am Speichern-Knopf oben, braucht keine
 * Satzsperre (zwei Bearbeiter kommen sich nicht in die Quere; meinen sie dasselbe
 * Unternehmen, entscheidet der eindeutige Schlüssel `uq_cr_pair` in der Datenbank) und
 * trägt keine `version` des Kunden.
 *
 * **Was er NICHT kann: archivieren** (Festlegung K2). Es gibt keinen Server-Weg dafür —
 * `packages/api/src/routes/customers.ts` kennt genau einen Beziehungsweg, das `POST`.
 * Ein Archivieren-Knopf bräuchte Route, Repository, Vertrag und erzeugten Client; das
 * ist ein eigenes Paket.
 *
 * **Die Kundennummer wird VERGEBEN.** Die Maske schickt `customerNumber: null`, und der
 * Server zieht die nächste Nummer aus dem Kreis des gewählten Unternehmens
 * (`allocateNumber`). Ein Eingabefeld dafür gibt es hier bewusst NICHT: Beim ANLEGEN
 * eines Kunden trägt man eine mitgebrachte Altnummer ein, an einer NACHTRÄGLICHEN
 * Zuordnung kommt sie aus dem Kreis. Deshalb steht dort „wird vergeben" statt eines
 * leeren Feldes.
 *
 * **Die Rolle ist wählbar, Vorgabe Kunde** (Entscheid Dirk; seit den Rollen am Geschäftspartner, C9): Wer
 * einem bestehenden Geschäftspartner ein weiteres Unternehmen zuordnet, tut das, weil dort Geschäft entsteht — als
 * Kunde (ACTIVE) oder als Lieferant. Den Lebenslauf (Ruhend, Ehemalig) ändert „Rollen ändern“ an der Beziehung.
 *
 * **Rollen je Unternehmen** (Lieferanten Strang A, Aufgabe 6): Jede Zeile trägt die Rollen als Kennzeichen,
 * „Rollen ändern“ (views/partners/RelationRoles.tsx) und den Abschnitt „Nummern“ — Kundennummer und Debitor nur mit
 * Kundenrolle, die Kreditornummer nur mit Lieferantenrolle; die Rechnungsangaben nur mit Kundenrolle (Belege gehen
 * an die Kundenrolle, der Server antwortet für reine Lieferanten 404).
 *
 * **Keine Rollenprüfung** (K4): Wie beim Anlegen entscheidet der Server (403
 * `UNTERNEHMEN_NICHT_ERLAUBT`), nicht die Maske.
 *
 * Die RECHNUNGSANGABEN (Migration 053, E342) stehen je Beziehung in der Zeile — Bauteil
 * BeziehungRechnungsangaben.
 *
 * **Eigener CSS-Block `kbz`** — er gehört allein dieser Datei.
 */

/** Die Klientenwege, die dieser Abschnitt braucht — so lässt er sich im Test mit ein paar
 *  `vi.fn` zeichnen (Muster `PortalMandantenClient`). `setDebitorennummer` kam mit der
 *  Debitorennummer je Beziehung dazu (Aufgabe 9, Ruling D15). */
export type BeziehungenClient =
  Pick<ApiClient, "addRelation" | "getCustomer" | "setDebitorennummer"> & RechnungsangabenClient & RelationRolesClient;

export type KundeBeziehungenProps = {
  client: BeziehungenClient;
  /** Der Kunde, dessen Zuordnungen hier stehen. Ein Wechsel setzt ALLES zurück. */
  kunde: KundeAkte;
  /** Die sichtbaren Konzernunternehmen (aus der Sitzung). Ohne Liste gibt es nichts
   *  zuzuordnen, und der Abschnitt zeigt nur den Satz. */
  companies: readonly Unternehmen[];
  /**
   * Über diesem Abschnitt steht ein FREMDES Panel — das Stammdaten-Formular. Dann ist
   * „Unternehmen zuordnen" nicht anklickbar, und ein Knopf, den niemand drücken kann,
   * gehört gesperrt (dieselbe Überlegung wie `verdeckt` in `KundePortalMandanten`; in
   * jsdom fängt nichts den Klick ab, ein Test griffe sonst hinter das Panel).
   */
  verdeckt?: boolean;
  /** Der FRISCHE Kundensatz nach einer erfolgreichen Zuordnung — er trägt die neue
   *  Beziehung und damit den neuen Kürzel-Chip im Kopf der Akte. */
  onAktualisiert: (akte: KundeAkte) => void;
};

/** Der Entwurf im Zuordnen-Panel. Die Kundennummer steht nicht darin — sie ist keine Eingabe
 *  (siehe Kopfkommentar). */
type Entwurf = { companyId: string; customerSince: string; rollen: RoleChoice };

/** Vorgabe beim Zuordnen: Kunde (ACTIVE), wie vor den Lieferantenrollen. */
const ZUORDNEN_VORGABE: RoleChoice = { customer: "ACTIVE", supplier: false };

/** Die Feldnamen, die eine 422 dieses Weges nennen kann UND die diese Maske zeigt.
 *  Alles andere (`customerNumber` aus KEIN_NUMMERNKREIS/NUMMER_VERGEBEN, `customerId`)
 *  landet im Meldungsband — unsichtbar darf kein Befund bleiben. */
type FeldName = "companyId" | "customerSince" | "status" | "supplierStatus";

/** Der Entwurf des Debitorennummer-Modals — „Nummer eintragen" (Ruling D15). „Nächste
 *  Nummer" braucht keinen Entwurf: Sie hat kein Feld und läuft deshalb ohne Panel, wie
 *  im Abgleich von DatevKopplung.tsx.
 *
 *  `urspruenglich` hält den WERT BEIM ÖFFNEN fest (Muster `entwurfAus`/`geaendert` in
 *  verwaltung/datev.tsx): Bei einer bereits zugeordneten Beziehung ist das Feld von
 *  Anfang an nicht leer — „geändert" heisst hier „weicht vom Ausgangswert ab", nicht
 *  „ist nicht leer" (Fixrunde 1, Befund I1). Ohne dieses Feld fragte „Abbrechen" auch
 *  dann nach, wenn niemand etwas getippt hatte. */
type DebitorEntwurf = {
  relationId: string; companyName: string; debtorNumber: string; urspruenglich: string;
};

export function KundeBeziehungen(p: KundeBeziehungenProps): ReactElement {
  const [dialog, setDialog] = useState<Entwurf | null>(null);
  /** Meldung von „Nächste Nummer“ (ohne Panel, im Abschnitt). */
  const [meldung, setMeldung] = useState<string | null>(null);

  const [debitorDialog, setDebitorDialog] = useState<DebitorEntwurf | null>(null);
  /** Zähler „Adressen des Kunden geändert": Eine Neuanlage an einer Rechnungsangaben-Karte ändert den
   *  Rückfall ALLER Beziehungen — jede Karte lädt dann Rückfall und Auswahl nach (W1, Fixrunde 1). */
  const [adressStand, setAdressStand] = useState(0);
  /** Die relationId, für die GERADE „Nächste Nummer" läuft — der Riegel gegen einen
   *  Doppelklick, nicht gegen den gesamten Abschnitt (die übrigen Beziehungen bleiben
   *  bedienbar). */
  const [naechsteLaeuft, setNaechsteLaeuft] = useState<string | null>(null);
  const { bestaetigen } = useBestaetigung();

  /**
   * Der Riegel gegen Antworten, die zu spät kommen (Muster `KundePortalMandanten`):
   * Der Kundenwechsel zählt hoch und macht jede noch fliegende Antwort ungültig. Ohne
   * ihn schriebe eine Zuordnung, die während des Wechsels zurückkommt, ihre frische
   * Akte in eine Ansicht, die längst einem anderen Kunden gehört.
   */
  const lauf = useRef(0);
  const kundeId = p.kunde.id;

  /**
   * Formularzustände der zwei Panels (ME-Etappe 3, Aufgabe 3). Beide legen neu an bzw.
   * setzen einen Wert ohne Versionsvergleich — eine 409 gibt es hier nicht. `laeuft` sperrt
   * Felder und Fuß; eine 422 legt `companyId`/`customerSince` bzw. `debtorNumber` ans Feld
   * (die doppelte Beziehung gehört AN DIE AUSWAHL, Festlegung K3), alles andere —
   * `customerNumber` bei KEIN_NUMMERNKREIS, KOPPLUNG_AUS ohne Feld — in die Meldung.
   */
  const zuordnen = useFormularZustand<KundeAkte | null>({
    speichern: async () => {
      const e = dialog;
      if (e === null) return null;
      await p.client.addRelation(kundeId, {
        companyId: e.companyId,
        // NULL heisst „aus dem Nummernkreis vergeben" — siehe Kopfkommentar.
        customerNumber: null,
        ...rolesOf(e.rollen),
        customerSince: e.rollen.customer === null || e.customerSince.trim() === "" ? null : e.customerSince.trim(),
      });
      /*
       * Die Antwort des Schreibwegs trägt NUR die vergebene Nummer. Die Kürzel-Chips im
       * Kopf der Akte entstehen aus `kunde.relations` — also den frischen Kundensatz
       * holen und nach oben melden, statt die Beziehung hier zusammenzubasteln. Ein
       * selbst gebauter Satz wäre eine zweite Wahrheit, und die erste Abweichung fiele
       * erst beim nächsten Laden auf.
       */
      return p.client.getCustomer(kundeId);
    },
    neuLaden: async () => { /* keine 409 auf diesem Weg */ },
    bekannteFelder: ["companyId", "customerSince", "status", "supplierStatus"] satisfies FeldName[],
    // KEIN_NUMMERNKREIS nennt `customerNumber` — ein Feld, das diese Maske nicht zeigt. In
    // der Meldung steht dann das deutsche Wort, nicht der Feldname (Fixrunde 1, W2).
    beschriftungen: { customerNumber: "Kundennummer" },
  });
  const speichert = zuordnen.laeuft;
  const feldFehler = zuordnen.feldFehler as Partial<Record<FeldName, string>>;

  const debitor = useFormularZustand<KundeAkte | null>({
    speichern: async () => {
      const d = debitorDialog;
      if (d === null) return null;
      await p.client.setDebitorennummer(kundeId, d.relationId,
        { modus: "WERT", debtorNumber: d.debtorNumber.trim() === "" ? null
          : d.debtorNumber.trim() });
      // Danach die FRISCHE Akte (siehe `debitorAktualisieren`).
      return p.client.getCustomer(kundeId);
    },
    neuLaden: async () => { /* keine 409 auf diesem Weg */ },
    bekannteFelder: ["debtorNumber"],
  });
  const debitorSpeichert = debitor.laeuft;

  /*
   * KEIN ZUSTAND ÜBERLEBT DEN KONTEXTWECHSEL (Hausregel). Ein halb ausgefülltes
   * „Unternehmen zuordnen", das den Wechsel überlebt, hängte die Beziehung an den
   * falschen Kunden. Auch das Abräumen zählt hoch: Beim Ausbauen der Ansicht sind alle
   * fliegenden Antworten ungültig, nicht erst beim nächsten Kunden.
   */
  useEffect(() => {
    lauf.current += 1;
    setDialog(null);
    setMeldung(null);
    setDebitorDialog(null);
    setNaechsteLaeuft(null);
    return () => { lauf.current += 1; };
  }, [kundeId]);

  /**
   * Die Unternehmen OHNE Beziehung — verglichen über den `shortCode` (Festlegung K5):
   * `Zuordnung` trägt keine `companyId`, und denselben Vergleich macht `KundenAkte.tsx`
   * bereits für die Vorgangs-Unternehmen. Nach `active` wird NICHT gefiltert, genau wie
   * im Anlegeformular.
   */
  const offene = p.companies.filter(
    (c) => !p.kunde.relations.some((r) => r.shortCode === c.shortCode));

  const geaendert = dialog !== null && (dialog.customerSince.trim() !== ""
    || dialog.rollen.customer !== ZUORDNEN_VORGABE.customer || dialog.rollen.supplier !== ZUORDNEN_VORGABE.supplier);
  // Auch das ECHTE Verlassen (Tab zu, neu laden) fragt nach — wie an jeder anderen Maske.
  useBeforeUnload(geaendert);

  function oeffnen(): void {
    const erstes = offene[0];
    if (erstes === undefined) return;
    setDialog({ companyId: erstes.id, customerSince: "", rollen: ZUORDNEN_VORGABE });
    zuordnen.zuruecksetzen();
  }

  /** Der EINE Ausgang neben dem Speichern (Hausregel „striktes Modalpanel"): kein
   *  Ausgang unter laufendem Schreiben, Rückfrage bei ungespeicherten Eingaben. Knopf
   *  und Escape gehen durch diese Funktion. */
  async function abbrechen(): Promise<void> {
    if (speichert) return;
    if (!(await darfAbbrechen(geaendert))) return;
    setDialog(null);
  }

  async function beiSpeichern(): Promise<void> {
    if (dialog === null || !hasRole(dialog.rollen)) return;
    const meiner = lauf.current;
    const frisch = await zuordnen.aktion();
    // Beim Kundenwechsel ist die Antwort ungültig (siehe `lauf`); Befunde stehen am Feld
    // oder in der Meldung des Panels.
    if (meiner !== lauf.current || frisch === undefined || frisch === null) return;
    setDialog(null);
    p.onAktualisiert(frisch);
  }

  /**
   * Nach einer Debitorennummer-Änderung die FRISCHE Akte holen — derselbe Grund wie nach
   * dem Zuordnen (siehe `beiSpeichern`): Die Antwort des Schreibwegs trägt nur die
   * Nummer, nicht die ganze Beziehungszeile, und ein selbst gebauter Satz wäre eine
   * zweite Wahrheit.
   */
  async function debitorAktualisieren(meiner: number): Promise<void> {
    const frisch = await p.client.getCustomer(kundeId);
    if (meiner !== lauf.current) return;
    p.onAktualisiert(frisch);
  }

  /**
   * „Nächste Nummer" — KEIN Modalpanel (Muster DatevKopplung.tsx, Abschnitt „Debitoren"):
   * Sie trägt kein Feld, das eine Fehleingabe tragen könnte, und zieht nur aus dem Kreis
   * des Unternehmens dieser Beziehung. Seit Entscheid 686 fragt sie aber vorher — wie die
   * nächste Kreditornummer (`RelationRoles.tsx`): Eine gezogene Nummer geht nicht zurück.
   */
  async function naechsteNummer(relationId: string, companyName: string, bisher: string | null): Promise<void> {
    if (!(await bestaetigen({ titel: PARTNER_TEXT.confirmDebtorNext(p.kunde.companyName),
      text: PARTNER_TEXT.debtorNextText(companyName, bisher),
      bestaetigenText: PARTNER_TEXT.debtorAssign, gefaehrlich: bisher !== null }))) return;
    const meiner = lauf.current;
    setNaechsteLaeuft(relationId);
    setMeldung(null);
    try {
      await p.client.setDebitorennummer(kundeId, relationId, { modus: "NAECHSTE" });
      await debitorAktualisieren(meiner);
    } catch (fehler) {
      if (meiner !== lauf.current) return;
      // Ein Befund am Feld (etwa KEINE_KUNDENROLLE an `debtorNumber`, die Rolle fiel inzwischen weg) nennt seinen Satz.
      setMeldung(fehler instanceof ApiError
        ? fehler.fields?.[0]?.message ?? fehler.message
        : "Die nächste Nummer liess sich nicht vergeben.");
    } finally {
      if (meiner === lauf.current) setNaechsteLaeuft(null);
    }
  }

  /** Öffnet das Modalpanel „Debitorennummer eintragen" für EINE Beziehung. */
  function debitorOeffnen(relationId: string, companyName: string, bisher: string | null): void {
    const wert = bisher ?? "";
    setDebitorDialog({ relationId, companyName, debtorNumber: wert, urspruenglich: wert });
    debitor.zuruecksetzen();
  }

  /** „Geändert" heisst: weicht vom WERT BEIM ÖFFNEN ab — nicht „ist nicht leer" (Befund I1). */
  const debitorGeaendert = debitorDialog !== null
    && debitorDialog.debtorNumber !== debitorDialog.urspruenglich;
  useBeforeUnload(debitorGeaendert);

  async function debitorAbbrechen(): Promise<void> {
    if (debitorSpeichert) return;
    if (!(await darfAbbrechen(debitorGeaendert))) return;
    setDebitorDialog(null);
  }

  async function debitorEintragen(): Promise<void> {
    if (debitorDialog === null) return;
    const meiner = lauf.current;
    const frisch = await debitor.aktion();
    if (frisch === undefined || frisch === null || meiner !== lauf.current) return;
    p.onAktualisiert(frisch);
    setDebitorDialog(null);
  }

  return (
    <section className="kbz" aria-label={PARTNER_TEXT.rolesPerCompany}>
      <h3 className="kbz__titel">{PARTNER_TEXT.rolesPerCompany}</h3>
      {/* Der Satz über alle Beziehungen, die Rollen mit den Wörtern der Kennzeichen darunter (relationsSentence). */}
      <p className="kbz__zeile">{relationsSentence(p.kunde.relations)}</p>

      {/*
        * DIE DEBITORENNUMMER (Migration 028, Ruling D15). Sie steht HIER und nicht in den
        * Stammdaten des Kunden, weil sie zur BEZIEHUNG gehört: Dieselbe Firma kann bei
        * mehreren Konzernunternehmen Kunde sein, und jedes ist bei DATEV ein eigener
        * Mandant mit eigenem Nummernkreis.
        *
        * Zwei Wege: „Nächste Nummer" zieht aus dem Kreis (ein Klick, kein Panel — sie
        * trägt kein Feld), „Nummer eintragen" nimmt eine mitgebrachte im STRIKTEN
        * Modalpanel. Ohne eingerichtete Kopplung antwortet der Server 422 KOPPLUNG_AUS —
        * die Meldung sagt dann, wo sie eingerichtet wird (Verwaltung → Unternehmen).
        */}
      {p.kunde.relations.length > 0 && (
        <ul className="kbz__beziehungen">
          {p.kunde.relations.map((r) => {
            const kundenrolle = r.status != null;
            return (
              <li key={r.id} className="kbz__beziehung">
                <span className="kbz__rollen">
                  <Chip className="kbz__marke" {...hausfarbeChip(p.companies, r.shortCode)}>{r.shortCode}</Chip>
                  <PartnerRoleBadges relation={r} mitKuerzel={false} />
                  <ChangeRoles client={p.client} kunde={p.kunde} relation={r} gesperrt={p.verdeckt === true}
                    onAktualisiert={p.onAktualisiert} />
                </span>
                <div className="kbz__nummern" role="group" aria-label={`${PARTNER_TEXT.numbers} ${r.shortCode}`}>
                  {kundenrolle && (
                    <>
                      <span className="kbz__nummer">{r.customerNumber}</span>
                      <span className="dtv__wert">
                        {r.debtorNumber == null ? "Kein Debitor" : `Debitor ${r.debtorNumber}`}
                      </span>
                      <span className="kbz__aktionen">
                        {/* me-knopf (ME-Etappe 4, P4). */}
                        <KiKnopf deaktiviert={naechsteLaeuft !== null}
                          onClick={() => { void naechsteNummer(r.id, r.companyName, r.debtorNumber ?? null); }}>
                          Nächste Nummer
                        </KiKnopf>
                        <KiKnopf
                          onClick={() => debitorOeffnen(r.id, r.companyName, r.debtorNumber ?? null)}>
                          Nummer eintragen
                        </KiKnopf>
                      </span>
                    </>
                  )}
                  <CreditorNumber client={p.client} kunde={p.kunde} relation={r} onAktualisiert={p.onAktualisiert} />
                </div>
                {kundenrolle && (
                  <BeziehungRechnungsangaben client={p.client} kundeId={p.kunde.id} kundeName={p.kunde.companyName}
                    relationId={r.id} companyName={r.companyName} verdeckt={p.verdeckt === true}
                    adressStand={adressStand} onAdressenGeaendert={() => setAdressStand((n) => n + 1)} />
                )}
              </li>
            );
          })}
        </ul>
      )}

      {meldung !== null && dialog === null && <Hinweis art="fehler">{meldung}</Hinweis>}

      {offene.length === 0 ? (
        <p className="kbz__hinweis">
          Dieser Kunde ist bereits jedem sichtbaren Unternehmen zugeordnet.
        </p>
      ) : (
        <div className="kbz__aktionen">
          <KiKnopf art="primaer" deaktiviert={dialog !== null || p.verdeckt === true}
            onClick={oeffnen}>
            Unternehmen zuordnen
          </KiKnopf>
        </div>
      )}

      {dialog !== null && (
        <Modal titel={`${p.kunde.companyName} einem Unternehmen zuordnen`}
          onAbbrechen={abbrechen} abbrechenGesperrt={speichert}>
          {/* Der Inhalt trägt seinen eigenen senkrechten Abstand: Der gemeinsame Rahmen
              staffelt seine Kinder nicht (siehe Modal.css). */}
          {zuordnen.meldung !== null && <Hinweis art="fehler">{zuordnen.meldung}</Hinweis>}
          <Formular beschriftung="oben" {...zuordnen.formularProps}>
            <Auswahl id="kbz-companyId" name="kbz-companyId" beschriftung="Unternehmen"
              wert={dialog.companyId}
              {...(feldFehler.companyId !== undefined ? { fehler: feldFehler.companyId } : {})}
              onGeaendert={(d) => setDialog((alt) => (alt === null ? alt
                : { ...alt, companyId: d.wert }))}>
              {offene.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Auswahl>

            {/* Die Rolle der neuen Beziehung (C9, P2), Vorgabe Kunde; mindestens eine. */}
            <RoleChoiceField wert={dialog.rollen}
              onWert={(rollen) => setDialog((d) => (d === null ? d : { ...d, rollen }))}
              fehler={feldFehler.status ?? feldFehler.supplierStatus} />

            {/* Die Kundennummer ist KEINE Eingabe — sie steht trotzdem da, weil der Bearbeiter
                wissen soll, was gleich entsteht. `Feld` im Lesemodus zeichnet reinen Text, ohne
                `<input>` und ohne `id`. Nummer und „Kunde seit“ nur mit Kundenrolle. */}
            {dialog.rollen.customer !== null && (
              <Feld id="kbz-customerNumber" label="Kundennummer" wert="wird vergeben"
                bearbeitbar={false} onChange={() => {}} />
            )}

            {dialog.rollen.customer !== null && (
              <Feld
                id="kbz-customerSince"
                label="Kunde seit"
                typ="date"
                wert={dialog.customerSince}
                bearbeitbar
                onChange={(wert) =>
                  setDialog((d) => (d === null ? d : { ...d, customerSince: wert }))}
                {...(feldFehler.customerSince !== undefined
                  ? { fehler: feldFehler.customerSince } : {})}
              />
            )}
          </Formular>

          {/* Die zwei einzigen Ausgänge. `abbrechenGesperrt` steht an BEIDEN Stellen —
              am Rahmen für die Escape-Taste, hier für den Knopf (ein Riegel nur am Knopf
              liesse die Tastatur daran vorbei). */}
          <ModalFuss speichernText="Zuordnen"
            speichernGesperrt={speichert || !hasRole(dialog.rollen)}
            abbrechenGesperrt={speichert}
            onSpeichern={() => { void beiSpeichern(); }}
            onAbbrechen={abbrechen} />
        </Modal>
      )}

      {debitorDialog !== null && (
        <Modal titel={`Debitorennummer für ${debitorDialog.companyName}`}
          onAbbrechen={debitorAbbrechen} abbrechenGesperrt={debitorSpeichert}>
          {debitor.meldung !== null && <Hinweis art="fehler">{debitor.meldung}</Hinweis>}
          <Formular beschriftung="oben" {...debitor.formularProps}>
            {/* Bewusst OHNE Bereichsprüfung: Länge und Nummernkreis sind je Unternehmen
                verschieden und änderbar — geprüft wird auf dem SERVER gegen die
                eingerichtete Kopplung (dieselbe Stelle wie in DatevKopplung.tsx). */}
            <Feld id="kbz-debitornummer" label="Debitorennummer" bearbeitbar
              wert={debitorDialog.debtorNumber}
              fehler={debitor.feldFehler.debtorNumber}
              onChange={(wert) => setDebitorDialog((d) => (d === null ? d
                : { ...d, debtorNumber: wert }))} />
          </Formular>
          <ModalFuss speichernText="Eintragen"
            speichernGesperrt={debitorSpeichert}
            abbrechenGesperrt={debitorSpeichert}
            onSpeichern={() => { void debitorEintragen(); }}
            onAbbrechen={debitorAbbrechen} />
        </Modal>
      )}
    </section>
  );
}
